import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, type Control, type FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  DEFAULT_PRICES,
  MAX_RAPPEN_PER_FIELD,
  nightDatesBetween,
  type ExternalStayInput,
  type PricesUpdate,
} from '@baerenfang/shared';
import {
  ApiClientError,
  fetchAvailability,
  submitExternalStay,
  type AvailabilityCell,
  type CapacityDetails,
} from '../lib/api';
import { todayInZurich } from '../lib/today';
import { DateRangePicker } from '../components/DateRangePicker';
import { NightsEditor } from '../components/NightsEditor';
import { addDays } from '../lib/useDateRange';
import {
  blankNight,
  formatChfFromRappen,
  parseChf,
  sumNight,
  type NightRow,
} from '../lib/stayFormHelpers';

type ExternalFormValues = {
  // Required identity fields. The new external form is for off-roster guests
  // who must record their full contact details so the warden can follow up
  // (cash reconciliation, invoice via post, etc.).
  name: string;
  surname: string;
  street: string;
  zipcode: string;
  town: string;
  email: string;
  // Rest mirrors StayForm exactly.
  check_in: string;
  check_out: string;
  nights: NightRow[];
  stayover_chf: string;
  beverages_chf: string;
  pay_twint: boolean;
  pay_slip: boolean;
  pay_cash: boolean;
  notes: string;
};

const inputClass =
  'w-full rounded-md border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 dark:focus:ring-slate-400';

export function ExternalStayForm() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const today = todayInZurich();
  // Walk-in default: the typical external guest is recording last night's
  // stay on their way out, so pre-fill a 1-night range covering yesterday.
  // The user can still pick a different range; the existing setFrom/setTo
  // coupling preserves the duration when they shift either endpoint.
  const yesterday = addDays(today, -1);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ExternalFormValues>({
    defaultValues: {
      name: '',
      surname: '',
      street: '',
      zipcode: '',
      town: '',
      email: '',
      check_in: yesterday,
      check_out: today,
      nights: [],
      stayover_chf: '',
      beverages_chf: '',
      pay_twint: false,
      pay_slip: false,
      pay_cash: false,
      notes: '',
    },
  });

  const check_in = watch('check_in');
  const check_out = watch('check_out');
  const nights = watch('nights');
  const stayover_chf = watch('stayover_chf');
  const beverages_chf = watch('beverages_chf');
  const pay_twint = watch('pay_twint');
  const pay_slip = watch('pay_slip');

  // External guests are off-roster so we don't know any per-user prices.
  // Use the project-wide defaults; the admin can adjust the saved row later.
  const prices: PricesUpdate = DEFAULT_PRICES;

  const [stayoverEditedByUser, setStayoverEditedByUser] = useState(false);

  // Inline, NOT memoised: see comment in StayForm.tsx for context — RHF
  // mutates the nights array in place and useMemo would return a stale value.
  let suggestedStayoverRappen = 0;
  for (const n of nights) {
    suggestedStayoverRappen +=
      (Number(n.members_adult) || 0) * prices.members_adult +
      (Number(n.members_child) || 0) * prices.members_child +
      (Number(n.guests_adult) || 0) * prices.guests_adult +
      (Number(n.guests_child) || 0) * prices.guests_child;
  }

  useEffect(() => {
    if (stayoverEditedByUser) return;
    if (nights.length === 0) return;
    setValue('stayover_chf', formatChfFromRappen(suggestedStayoverRappen), {
      shouldDirty: false,
      shouldTouch: false,
    });
  }, [suggestedStayoverRappen, nights.length, setValue, stayoverEditedByUser]);

  const resetStayoverSuggestion = () => setStayoverEditedByUser(false);

  const amountTotalChf = useMemo(() => {
    const s = parseChf(stayover_chf);
    const b = parseChf(beverages_chf);
    if (!Number.isFinite(s) || !Number.isFinite(b)) return null;
    if (s === 0 && b === 0) return null;
    return ((s + b) / 100).toFixed(2);
  }, [stayover_chf, beverages_chf]);

  useEffect(() => {
    if (!check_in || !check_out || check_in >= check_out) {
      if (getValues('nights').length > 0) {
        setValue('nights', [], { shouldDirty: true });
      }
      return;
    }
    const dates = nightDatesBetween(check_in, check_out);
    const existing = getValues('nights');
    const next = dates.map((d) => existing.find((n) => n.date === d) ?? blankNight(d));
    const same =
      next.length === existing.length && next.every((n, i) => n.date === existing[i]!.date);
    if (!same) setValue('nights', next, { shouldDirty: true });
  }, [check_in, check_out, setValue, getValues]);

  useEffect(() => {
    setStayoverEditedByUser(false);
  }, [check_in, check_out]);

  const [availability, setAvailability] = useState<Map<string, AvailabilityCell>>(new Map());
  useEffect(() => {
    if (!check_in || !check_out || check_in >= check_out) {
      setAvailability(new Map());
      return;
    }
    const dates = nightDatesBetween(check_in, check_out);
    if (dates.length === 0) return;
    const last = dates[dates.length - 1]!;
    if (last < today) {
      setAvailability(new Map());
      return;
    }
    const first = dates[0]! < today ? today : dates[0]!;
    const ctl = new AbortController();
    fetchAvailability(first, last, ctl.signal)
      .then((rows) => {
        const m = new Map<string, AvailabilityCell>();
        for (const r of rows) m.set(r.date, r);
        setAvailability(m);
      })
      .catch(() => {
        // Soft warning only — ignore errors; server is source of truth.
      });
    return () => ctl.abort();
  }, [check_in, check_out, today]);

  let personNightsTotal = 0;
  for (const n of nights) personNightsTotal += sumNight(n);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [capacityErrors, setCapacityErrors] = useState<CapacityDetails | null>(null);

  const defaultOpenDate = useMemo(() => {
    if (nights.length === 0) return null;
    const todayInRange = nights.find((n) => n.date === today);
    return (todayInRange ?? nights[0]!).date;
  }, [nights, today]);

  const onSubmit = handleSubmit(async (data) => {
    setSubmitError(null);
    setCapacityErrors(null);

    const stayover_rappen = parseChf(data.stayover_chf);
    const beverages_rappen = parseChf(data.beverages_chf);
    if (
      !Number.isFinite(stayover_rappen) ||
      !Number.isFinite(beverages_rappen) ||
      stayover_rappen > MAX_RAPPEN_PER_FIELD ||
      beverages_rappen > MAX_RAPPEN_PER_FIELD
    ) {
      setSubmitError(t('stayForm.invalidAmount'));
      return;
    }

    const payload: ExternalStayInput = {
      name: data.name.trim(),
      surname: data.surname.trim(),
      street: data.street.trim(),
      zipcode: data.zipcode.trim(),
      town: data.town.trim(),
      email: data.email.trim(),
      check_in: data.check_in,
      check_out: data.check_out,
      nights: data.nights.map((n) => ({
        date: n.date,
        members_adult: Number(n.members_adult) || 0,
        members_child: Number(n.members_child) || 0,
        guests_adult: Number(n.guests_adult) || 0,
        guests_child: Number(n.guests_child) || 0,
      })),
      stayover_rappen,
      beverages_rappen,
      pay_twint: data.pay_twint,
      pay_slip: data.pay_slip,
      pay_cash: data.pay_cash,
      notes: data.notes.trim() ? data.notes.trim() : null,
    };

    try {
      const result = await submitExternalStay(payload);
      navigate('/gast-abrechnung/bestaetigung', {
        replace: true,
        state: {
          stay: { id: result.id, ...payload },
          name: `${payload.name} ${payload.surname}`.trim(),
          external: true,
        },
      });
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 409 && e.code === 'ERR_CAPACITY') {
        setCapacityErrors((e.details as CapacityDetails) ?? {});
        setSubmitError(t('errors.ERR_CAPACITY'));
      } else if (e instanceof ApiClientError && e.status === 401) {
        navigate('/gast', { replace: true });
      } else if (e instanceof ApiClientError) {
        setSubmitError(t(`errors.${e.code}`, e.message));
      } else {
        setSubmitError(t('errors.ERR_INTERNAL'));
      }
    }
  });

  const horizon = useMemo(() => {
    const [y, m, d] = today.split('-').map(Number);
    const dt = new Date(Date.UTC(y!, m! - 1 + 12, d!));
    return dt.toISOString().slice(0, 10);
  }, [today]);

  const hasNights = nights.length > 0;

  return (
    <section aria-labelledby="ext-stayform-title">
      <h1 id="ext-stayform-title" className="text-xl sm:text-2xl font-semibold mb-3">
        {t('externalStayForm.title')}
      </h1>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
        {t('externalStayForm.intro')}
      </p>

      <form onSubmit={onSubmit} className="space-y-5">
        <fieldset className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-3 bg-white dark:bg-slate-900">
          <legend className="px-2 text-sm font-medium">
            {t('externalStayForm.contactSection')}
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-sm font-medium mb-1">
                {t('externalStayForm.name')} <span className="text-rose-600">*</span>
              </span>
              <input
                type="text"
                autoComplete="given-name"
                {...register('name', { required: true, maxLength: 120 })}
                className={inputClass}
              />
              {errors.name && (
                <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">
                  {t('externalStayForm.required')}
                </p>
              )}
            </label>
            <label className="block">
              <span className="block text-sm font-medium mb-1">
                {t('externalStayForm.surname')} <span className="text-rose-600">*</span>
              </span>
              <input
                type="text"
                autoComplete="family-name"
                {...register('surname', { required: true, maxLength: 120 })}
                className={inputClass}
              />
              {errors.surname && (
                <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">
                  {t('externalStayForm.required')}
                </p>
              )}
            </label>
          </div>
          <label className="block">
            <span className="block text-sm font-medium mb-1">
              {t('externalStayForm.street')} <span className="text-rose-600">*</span>
            </span>
            <input
              type="text"
              autoComplete="street-address"
              {...register('street', { required: true, maxLength: 200 })}
              className={inputClass}
            />
            {errors.street && (
              <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">
                {t('externalStayForm.required')}
              </p>
            )}
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block col-span-1">
              <span className="block text-sm font-medium mb-1">
                {t('externalStayForm.zipcode')} <span className="text-rose-600">*</span>
              </span>
              <input
                type="text"
                autoComplete="postal-code"
                {...register('zipcode', { required: true, maxLength: 20 })}
                className={inputClass}
              />
              {errors.zipcode && (
                <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">
                  {t('externalStayForm.required')}
                </p>
              )}
            </label>
            <label className="block col-span-2">
              <span className="block text-sm font-medium mb-1">
                {t('externalStayForm.town')} <span className="text-rose-600">*</span>
              </span>
              <input
                type="text"
                autoComplete="address-level2"
                {...register('town', { required: true, maxLength: 120 })}
                className={inputClass}
              />
              {errors.town && (
                <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">
                  {t('externalStayForm.required')}
                </p>
              )}
            </label>
          </div>
          <label className="block">
            <span className="block text-sm font-medium mb-1">
              {t('externalStayForm.email')} <span className="text-rose-600">*</span>
            </span>
            <input
              type="email"
              autoComplete="email"
              {...register('email', {
                required: true,
                maxLength: 200,
                pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              })}
              className={inputClass}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-rose-700 dark:text-rose-300">
                {t('externalStayForm.invalidEmail')}
              </p>
            )}
          </label>
        </fieldset>

        <div>
          <label className="block min-w-0">
            <span className="block text-sm font-medium mb-1">
              {t('stayForm.checkIn')} – {t('stayForm.checkOut')}
            </span>
            {/* One Controller on check_in keeps the `required` rule; the picker
                emits both ends atomically. minSpan 1 enforces check_out >
                check_in. The walk-in default (check_in: yesterday,
                check_out: today) lives in useForm defaultValues. */}
            <Controller
              control={control}
              name="check_in"
              rules={{ required: true }}
              render={({ field }) => (
                <DateRangePicker
                  from={check_in}
                  to={check_out}
                  onChange={(ci, co) => {
                    setValue('check_in', ci, { shouldDirty: true });
                    setValue('check_out', co, { shouldDirty: true });
                    field.onChange(ci);
                  }}
                  minSpanDays={1}
                  max={horizon}
                  required
                />
              )}
            />
          </label>
        </div>

        {hasNights ? (
          <NightsEditor
            nights={nights}
            control={control as unknown as Control<FieldValues>}
            defaultOpenDate={defaultOpenDate}
            availability={availability}
            capacityErrors={capacityErrors}
            lang={i18n.resolvedLanguage ?? 'de'}
            prices={prices}
          />
        ) : (
          <p className="text-sm text-slate-500 bg-slate-50 border border-slate-200 dark:text-slate-400 dark:bg-slate-900 dark:border-slate-700 rounded-md px-3 py-3">
            {t('stayForm.choosePrompt')}
          </p>
        )}

        {hasNights && (
          <div className="text-right text-sm font-medium">
            {t('stayForm.totalPersonNights', { count: personNightsTotal })}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="flex items-baseline justify-between gap-2 mb-1">
              <span className="text-sm font-medium">{t('stayForm.stayoverChf')}</span>
              {stayoverEditedByUser && (
                <button
                  type="button"
                  onClick={resetStayoverSuggestion}
                  className="text-xs text-slate-500 dark:text-slate-400 underline decoration-dotted underline-offset-2"
                >
                  {t('stayForm.useSuggestedAmount')}
                </button>
              )}
            </span>
            <Controller
              control={control}
              name="stayover_chf"
              render={({ field }) => (
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]+([.,][0-9]{1,2})?"
                  autoComplete="off"
                  placeholder="0.00"
                  value={field.value ?? ''}
                  onBlur={field.onBlur}
                  onChange={(e) => {
                    if (!stayoverEditedByUser) setStayoverEditedByUser(true);
                    field.onChange(e.target.value);
                  }}
                  className={inputClass}
                />
              )}
            />
          </label>
          <label className="block">
            <span className="block text-sm font-medium mb-1">{t('stayForm.beveragesChf')}</span>
            <input
              type="text"
              inputMode="decimal"
              pattern="[0-9]+([.,][0-9]{1,2})?"
              autoComplete="off"
              placeholder="0.00"
              {...register('beverages_chf')}
              className={inputClass}
            />
          </label>
        </div>

        {amountTotalChf != null && (
          <div className="text-right text-lg font-semibold tabular-nums">
            {t('stayForm.amountTotal', { total: amountTotalChf })}
          </div>
        )}

        <fieldset className="space-y-2">
          <legend className="block text-sm font-medium mb-1">
            {t('stayForm.paymentMethod')}
          </legend>
          <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
            {t('stayForm.paymentMethodHelp')}
          </p>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...register('pay_twint')} />
              <span>{t('stayForm.paymentTwint')}</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...register('pay_slip')} />
              <span>{t('stayForm.paymentSlip')}</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...register('pay_cash')} />
              <span>{t('stayForm.paymentCash')}</span>
            </label>
          </div>
          {pay_twint && (
            <div className="mt-3 flex flex-col items-start gap-2">
              <p className="text-xs text-slate-600 dark:text-slate-400">
                {t('stayForm.paymentTwintQrHint')}
              </p>
              <img
                src="/twint-qrcode.svg"
                alt={t('stayForm.paymentTwintQrAlt')}
                className="w-44 sm:w-52 bg-white p-2 rounded-md border border-slate-200"
              />
            </div>
          )}
          {pay_slip && (
            <div className="mt-3 flex flex-col items-start gap-2">
              <p className="text-xs text-slate-600 dark:text-slate-400">
                {t('stayForm.paymentSlipQrHint')}
              </p>
              <img
                src="/bank-qrcode.svg"
                alt={t('stayForm.paymentSlipQrAlt')}
                className="w-44 sm:w-52 bg-white p-2 rounded-md border border-slate-200"
              />
            </div>
          )}
        </fieldset>

        <label className="block">
          <span className="block text-sm font-medium mb-1">{t('stayForm.notes')}</span>
          <textarea
            rows={3}
            maxLength={2000}
            {...register('notes')}
            className={inputClass}
          />
        </label>

        {submitError && (
          <p
            role="alert"
            className="text-sm text-rose-700 bg-rose-50 border border-rose-300 dark:text-rose-300 dark:bg-rose-950 dark:border-rose-700 rounded-md px-3 py-2"
          >
            {submitError}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !hasNights}
          className="w-full sm:w-auto rounded-md bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 font-medium px-6 py-2.5 disabled:opacity-60"
        >
          {isSubmitting ? t('common.loading') : t('stayForm.submit')}
        </button>
      </form>
    </section>
  );
}
